import { FilterCondition, TFilterValue } from "@irohana-dev/components";
import { FC, useCallback, useMemo } from "react";
import { ConfirmDeleteModal } from "@/components/commons/ConfirmDeleteModal";
import { useFilterConditionPanel } from "@/components/commons/filter-condition/use-filter-condition-panel";
import { TFilterConditionPanelConfig } from "@/components/commons/filter-condition/types";
import {
  buildCompanyFilterFields,
  fromFilterValue,
  toFilterValue,
} from "@/features/company/components/company-list/company-filter-condition-adapter";
import {
  TCompanyDisplayColumnSetting,
  TCompanyFilterFormExtended,
  defaultCompanyDisplayColumns,
  defaultCompanyFilterValues,
  normalizeCompanyDateFields,
  normalizeCompanyMultiSelectFields,
  stripEmptyCompanyFilterFields,
  COMPANY_LOCKED_COLUMN_IDS,
} from "@/features/company/components/company-list/company-filter-shared";
import { useCompanyFilterMasterData } from "@/features/company/components/company-list/use-company-filter-master-data";
import { COMPANY_DISPLAY_COLUMNS_STORAGE_KEY } from "@/features/company/constants/common";
import { TSavedConditionShareScope } from "@/api/services/main/saved-condition/dtos/saved-condition.dto";
import { filterConditionShareColor } from "@/components/commons/filter-condition/filter-condition-share-color";
import { FILTER_CONDITION_PANEL_WIDTH } from "@/components/commons/filter-condition/filter-condition-panel-width";

const panelConfig: TFilterConditionPanelConfig<TCompanyFilterFormExtended, TCompanyDisplayColumnSetting> = {
  pageType: "company",
  defaultFilters: defaultCompanyFilterValues,
  defaultColumns: defaultCompanyDisplayColumns,
  columnsStorageKey: COMPANY_DISPLAY_COLUMNS_STORAGE_KEY,
  lockedColumnIds: COMPANY_LOCKED_COLUMN_IDS,
  normalizeFilters: (filters) => normalizeCompanyMultiSelectFields(normalizeCompanyDateFields(filters)),
  toFilterValue,
  fromFilterValue,
  stripEmptyFilters: stripEmptyCompanyFilterFields,
  getDateRange: (filters) => ({ from: filters.registrationDateFrom, to: filters.registrationDateTo }),
};

type Props = {
  isOpen: boolean;
  /** `create` is the フィルタ panel; `edit` is 保存条件を編集, which only saves the condition back. */
  mode?: "create" | "edit";
  onClose: () => void;
  onApplyFilters?: (filters: TCompanyFilterFormExtended) => void;
  onApplyDisplaySettings?: (columns: TCompanyDisplayColumnSetting[]) => void;
  initialFilters?: Partial<TCompanyFilterFormExtended>;
  initialDisplayColumns?: TCompanyDisplayColumnSetting[];
  /** Present when a saved condition is applied; saving then overwrites it instead of creating one. */
  appliedConditionId?: number | null;
  appliedConditionName?: string;
  /** The applied condition's own sharing settings, so editing one shows its values and not the last edit's. */
  appliedConditionShareScope?: TSavedConditionShareScope;
  appliedConditionIsDefault?: boolean;
  appliedConditionIncludeColumnSettings?: boolean;
  /** The column order/hidden pair stored with the condition, when it was saved with one. */
  appliedConditionColumnSettings?: { order?: string[]; hidden?: string[] } | null;
  /** Reports the just-saved condition so the toolbar can show it as the applied one. */
  onConditionSaved?: (id: number, title: string) => void;
};

export const CompanyFilterConditionPanel: FC<Props> = ({ mode = "create", ...rest }) => {
  const { truncatedOptions, getMunicipalityNameOptionsByPrefectures } = useCompanyFilterMasterData();
  const panel = useFilterConditionPanel(panelConfig, { ...rest, mode });

  const prefectures = useMemo(() => (Array.isArray(panel.value.prefecture) ? panel.value.prefecture : []), [panel.value.prefecture]);

  const cityOptions = useMemo(
    () => (prefectures.length > 0 ? getMunicipalityNameOptionsByPrefectures(prefectures) : []),
    [prefectures, getMunicipalityNameOptionsByPrefectures],
  );

  const fields = useMemo(
    () => buildCompanyFilterFields(truncatedOptions, cityOptions, prefectures.length > 0),
    [truncatedOptions, cityOptions, prefectures.length],
  );

  // Clearing the prefectures invalidates any municipality already chosen under them.
  const handleChange = useCallback(
    (next: TFilterValue) => {
      panel.setValue((prev) => {
        const prevPrefecture = Array.isArray(prev.prefecture) ? prev.prefecture : [];
        const nextPrefecture = Array.isArray(next.prefecture) ? next.prefecture : [];
        if (JSON.stringify(prevPrefecture) === JSON.stringify(nextPrefecture)) return next;
        const { city, ...restValue } = next;
        return restValue;
      });
    },
    [panel.setValue],
  );

  return (
    <>
      <FilterCondition
        width={FILTER_CONDITION_PANEL_WIDTH}
        shareColor={filterConditionShareColor}
        isOpen={rest.isOpen}
        mode={panel.displayMode}
        onClose={rest.onClose}
        fields={fields}
        value={panel.value}
        onChange={handleChange}
        columns={panel.columnOptions}
        columnOrder={panel.columnOrder}
        hiddenColumns={panel.hiddenColumns}
        lockedColumns={COMPANY_LOCKED_COLUMN_IDS}
        onColumnSettingsChange={panel.handleColumnSettingsChange}
        conditionName={panel.conditionName}
        onConditionNameChange={panel.handleConditionNameChange}
        conditionNameError={panel.conditionNameError}
        appliedConditionId={rest.appliedConditionId ?? undefined}
        shareScope={panel.shareScope}
        onShareScopeChange={panel.setShareScope}
        isDefault={panel.isDefault}
        onIsDefaultChange={panel.setIsDefault}
        includeColumnSettings={panel.includeColumnSettings}
        onIncludeColumnSettingsChange={panel.setIncludeColumnSettings}
        hasSavedColumnSettings={panel.hasSavedColumnSettings}
        onSaveCondition={panel.handleSaveCondition}
        isSaving={panel.isSaving}
        onApply={panel.handleApply}
        onClear={() => panel.hasDataToClear && panel.setIsClearConfirmOpen(true)}
      />
      <ConfirmDeleteModal
        open={panel.isClearConfirmOpen}
        onClose={() => panel.setIsClearConfirmOpen(false)}
        onOk={panel.clearValue}
        message="設定した条件がすべてクリアされますがよろしいですか？"
        title=""
      />
    </>
  );
};
